/**
 * Credit prices for Google's Nano Banana (Gemini image) models — the single
 * source of truth for every generate / edit / catalogue / estimate site.
 *
 * House convention: credits = provider cost ÷ $0.02 per credit, rounded UP to
 * a whole credit. Prices are Google's standard (non-batch) per-image rates as
 * listed on 2026-09-09 — identical on Vertex AI and the Gemini API:
 *
 *   Nano Banana 2 Lite  gemini-3.1-flash-lite-image  1K $0.0336        → 2
 *   Nano Banana 2       gemini-3.1-flash-image       0.5K $0.045       → 3
 *                                                    1K   $0.067       → 4
 *                                                    2K   $0.101       → 6
 *                                                    4K   $0.151       → 8
 *   Nano Banana Pro     gemini-3-pro-image           1K/2K $0.134      → 7
 *                                                    4K    $0.24       → 12
 *   Nano Banana         gemini-2.5-flash-image       $0.039            → 2
 *   Nano Banana 2.1     gemini-nano-banana-2.1       1K $0.0336        → 2
 *                       (added 2026-10-07)           2K $0.0504        → 3
 *                                                    4K $0.113         → 6
 *
 * Before 2026-09-09 these were 1 / 1-2-3-4 / 4-8 / 2, i.e. roughly half of
 * cost. Input-image tokens on edits ($0.50/M for Flash, $2/M for Pro — about
 * $0.001 per reference image on Pro) are absorbed. Nano Banana 2.1 bills input
 * at $1.50/M (1,120 tokens = $0.0017 per reference image), also absorbed. It
 * has no 0.5K tier: a 0.5K request runs (and bills) at 1K.
 *
 * Pure module (no imports) so client bundles can use it.
 */
export type NanoBananaResolution = '0.5K' | '1K' | '2K' | '4K';

export const NANO_BANANA_2_LITE_CREDITS = 2;

export const NANO_BANANA_2_CREDITS: Record<NanoBananaResolution, number> = {
  '0.5K': 3,
  '1K': 4,
  '2K': 6,
  '4K': 8,
};

export const NANO_BANANA_PRO_CREDITS: Record<
  Exclude<NanoBananaResolution, '0.5K'>,
  number
> = {
  '1K': 7,
  '2K': 7,
  '4K': 12,
};

/** Nano Banana 2.1 (gemini-nano-banana-2.1): 1K/2K/4K only, no 0.5K. */
export const NANO_BANANA_2_1_CREDITS: Record<
  Exclude<NanoBananaResolution, '0.5K'>,
  number
> = {
  '1K': 2,
  '2K': 3,
  '4K': 6,
};

/** Legacy Gemini 2.5 Flash Image. */
export const NANO_BANANA_CREDITS = 2;

/** Gemini 3 Flash image output (not in the catalogue; unpriced by Google). */
export const NANO_BANANA_FLASH_CREDITS = { default: 1, '4K': 4 } as const;

/**
 * Normalises a resolution or dimensions string ('4K', '4k', '2K', '1K',
 * '0.5K', '512', or something like '2K 16:9') to a catalogue resolution.
 * Anything unrecognised is treated as 1K, the default tier.
 */
export function normalizeNanoBananaResolution(
  value?: string | null,
): NanoBananaResolution {
  const v = (value || '').toUpperCase();
  if (v.includes('4K')) return '4K';
  if (v.includes('2K')) return '2K';
  if (v.includes('0.5K') || v.includes('512')) return '0.5K';
  return '1K';
}

export const nanoBanana2Credits = (resolution?: string | null): number =>
  NANO_BANANA_2_CREDITS[normalizeNanoBananaResolution(resolution)];

/** Nano Banana 2.1 has no 0.5K tier; anything below 1K runs at 1K. */
export const nanoBanana21Resolution = (
  resolution?: string | null,
): Exclude<NanoBananaResolution, '0.5K'> => {
  const normalized = normalizeNanoBananaResolution(resolution);
  return normalized === '0.5K' ? '1K' : normalized;
};

export const nanoBanana21Credits = (resolution?: string | null): number =>
  NANO_BANANA_2_1_CREDITS[nanoBanana21Resolution(resolution)];

export const nanoBananaProCredits = (resolution?: string | null): number =>
  normalizeNanoBananaResolution(resolution) === '4K'
    ? NANO_BANANA_PRO_CREDITS['4K']
    : NANO_BANANA_PRO_CREDITS['1K'];

export const nanoBananaFlashCredits = (resolution?: string | null): number =>
  normalizeNanoBananaResolution(resolution) === '4K'
    ? NANO_BANANA_FLASH_CREDITS['4K']
    : NANO_BANANA_FLASH_CREDITS.default;

/**
 * Credits for a catalogue model name, or null when the name is not a Nano
 * Banana model (callers fall through to their own pricing).
 */
export function getNanoBananaCredits(
  model: string,
  resolution?: string | null,
): number | null {
  switch (model) {
    case 'Nano Banana 2 Lite':
      return NANO_BANANA_2_LITE_CREDITS;
    case 'Nano Banana 2':
      return nanoBanana2Credits(resolution);
    case 'Nano Banana 2.1':
      return nanoBanana21Credits(resolution);
    case 'Nano Banana Pro':
      return nanoBananaProCredits(resolution);
    case 'Nano Banana Flash':
      return nanoBananaFlashCredits(resolution);
    case 'Nano Banana':
      return NANO_BANANA_CREDITS;
    default:
      return null;
  }
}
